import clsx from 'clsx'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { AdvertisementCardList } from '@/components/features/advertisement'
import { AnnouncementCardList } from '@/components/features/announcement'
import { ZendeskMessengerWidget } from '@/components/features/support/ZendeskMessengerWidget'
import { FavoriteIcon } from '@/components/icons/FavoriteIcon'
import { ScheduleOutlinedIcon } from '@/components/icons/ScheduleOutlined'
import { SearchFilledIcon } from '@/components/icons/SearchFilled'
import { PageWithHeader, PageWrapper } from '@/components/layout'
import {
  Icon,
  IconWrapper,
  Image,
  LinkButton,
  LoadingOverlay,
} from '@/components/ui'
import { ROUTE } from '@/const'
import { useAdvertisements } from '@/hooks/features/advertisement'
import { useLimitedMode } from '@/hooks/features/auth'
import { useUnapprovedOrders } from '@/hooks/features/order'
import { useMyStore } from '@/hooks/features/store'
import { useMe } from '@/hooks/features/user/me.altair'

export const TopPage = () => {
  const { t } = useTranslation()
  const unapprovedOrdersQuery = useUnapprovedOrders({})
  const advertisementsQuery = useAdvertisements({ displayPosition: 'top' })

  const { data: me } = useMe({})
  const { data: myStore } = useMyStore({})
  const { mode, isLimitedTerm } = useLimitedMode()

  const hasAlert = useMemo(() => {
    if (!unapprovedOrdersQuery.data) return false
    const unapprovedOrdersCount =
      unapprovedOrdersQuery.data.orderNos?.length ?? 0
    return unapprovedOrdersCount > 0
  }, [unapprovedOrdersQuery.data])

  const isLoading = useMemo(
    () => unapprovedOrdersQuery.isFetching,
    [unapprovedOrdersQuery.isFetching]
  )

  const showOrderHistory = mode === 'normal' || !isLimitedTerm
  const showFavorite = mode === 'normal' || !isLimitedTerm
  const showSearch = mode === 'normal' || !isLimitedTerm

  return (
    <PageWithHeader withSearch={true}>
      <ZendeskMessengerWidget />
      <PageWrapper withBreadcrumb={false} spacingX={'sm'}>
        <LoadingOverlay isLoading={isLoading} />

        <div
          className={clsx(
            'tw-p-4 tw-text-center tw-text-[15px]',
            'tw-pt-0',
            !showOrderHistory && !showFavorite && !showSearch && 'tw-pb-2'
          )}
        >
          {`${myStore?.storeName || '-'} ${t('user.store.honorific')}`}
        </div>

        <AdvertisementCardList
          advertisements={advertisementsQuery.data?.advertisements ?? []}
        />

        <AnnouncementCardList />

        <div
          className={clsx(
            !showOrderHistory && !showFavorite && !showSearch
              ? 'tw-mt-0'
              : 'tw-mt-16',
            'tw-flex tw-flex-col tw-gap-4'
          )}
        >
          <div
            className={clsx(
              !showOrderHistory && !showFavorite && !showSearch
                ? 'tw-mt-0'
                : 'tw-mt-8'
            )}
          >
            {showOrderHistory && (
              <div
                className={clsx(
                  'tw-relative',
                  'after:tw-absolute',
                  'tw-left-0',
                  hasAlert ? 'after:tw-top-[-71px]' : 'after:tw-top-[-73px]',
                  'after:tw-block',
                  "after:tw-content-['']",
                  hasAlert
                    ? 'after:tw-h-[72px] after:tw-w-[110px] after:tw-bg-kikumaAlert'
                    : 'after:tw-h-[75px] after:tw-w-[96px] after:tw-bg-kikumaTel',
                  'after:tw-bg-contain'
                )}
              >
                <LinkButton
                  href={ROUTE.orderHistories}
                  variant={hasAlert ? 'notice' : 'secondary'}
                  size={'xl'}
                >
                  <div className={clsx('tw-flex tw-flex-col tw-items-center')}>
                    <div
                      className={clsx('tw-flex tw-items-center tw-space-x-2')}
                    >
                      <IconWrapper>
                        <Icon icon={ScheduleOutlinedIcon} size={'2xl'} />
                      </IconWrapper>
                      <span>{t('top.menu.orderHistory')}</span>
                    </div>
                    {hasAlert && (
                      <div
                        className={clsx('tw-mt-1 tw-text-sm tw-text-alert-500')}
                      >{`${t('top.warning.orderHistory.approve')}`}</div>
                    )}
                  </div>
                </LinkButton>
              </div>
            )}
          </div>
          <div className={clsx('tw-flex tw-items-center tw-gap-2')}>
            {showFavorite && (
              <LinkButton
                href={ROUTE.favorites}
                variant={'secondary'}
                size={'xl'}
              >
                <IconWrapper>
                  <FavoriteIcon size={24} type="empty" />
                </IconWrapper>
                <span>{t('top.menu.favorite')}</span>
              </LinkButton>
            )}
            {showSearch && (
              <LinkButton href={ROUTE.search} variant={'secondary'} size={'xl'}>
                <IconWrapper>
                  <Icon icon={SearchFilledIcon} size={'2xl'} />
                </IconWrapper>
                <span>{t('top.menu.search')}</span>
              </LinkButton>
            )}
          </div>
          {!!myStore?.importOrder && (
            <div className={clsx('tw-flex tw-items-center tw-gap-2')}>
              <LinkButton
                href={ROUTE.orderImport}
                variant={'secondary'}
                size={'lg'}
              >
                {t('top.menu.orderImport')}
              </LinkButton>
              <LinkButton
                href={ROUTE.purchases}
                variant={'secondary'}
                size={'lg'}
              >
                {t('top.menu.ecOrderImport')}
              </LinkButton>
            </div>
          )}
          {!!me?.uploadOrder && (
            <div>
              <LinkButton
                href={ROUTE.purchasedInfo}
                variant={'secondary'}
                size={'lg'}
              >
                {t('top.menu.import')}
              </LinkButton>
            </div>
          )}
          {!!me?.invoice && (
            <div>
              <LinkButton
                href={ROUTE.invoice}
                variant={'secondary'}
                size={'lg'}
              >
                {t('top.menu.invoice')}
              </LinkButton>
            </div>
          )}
        </div>
        <div className={clsx('tw-mt-1', 'tw-flex tw-justify-end')}>
          <div className={clsx('tw-h-[169px] tw-w-[124px]')}>
            <Image src="/images/kikumi_cart.png" alt="" isEventNone={true} />
          </div>
        </div>
      </PageWrapper>
    </PageWithHeader>
  )
}
